/**
 * Shelf packing for the kit turntable (05 §5 rule 6): every model gets a circle of floor big
 * enough to turn in, circles go on shelves left to right, shelves stack north to south, and
 * when a page of floor is full the next page starts. Pure, so the layout is the same every
 * run and a frame saved from page 3 can be compared with last week's.
 */

export interface ShelfItem {
  name: string;
  /** Radius of the circle the model sweeps as it turns, metres. */
  radius: number;
}

export interface Placement {
  name: string;
  page: number;
  /** Centre of the item's circle, metres, with the page centred on the origin (+z south, toward the camera). */
  x: number;
  z: number;
}

export interface PageSize {
  width: number;
  depth: number;
  gap: number;
}

/** A little less than the 35.6 x 28.3 m the camera sees at 18 px/m, so nothing touches the frame. */
export const TURNTABLE_PAGE: PageSize = { width: 33, depth: 25, gap: 0.8 };

interface Shelf {
  page: number;
  /** Distance of the shelf's north edge from the page's north edge, and how deep and how full it is. */
  top: number;
  depth: number;
  used: number;
  items: Array<{ name: string; d: number }>;
}

/** Biggest first, so the hangar does not end up alone on the last page with a crate. Stable for equal sizes. */
export function packShelves(items: readonly ShelfItem[], page: PageSize = TURNTABLE_PAGE): Placement[] {
  const order = items.map((item, i) => ({ item, i })).sort((a, b) => b.item.radius - a.item.radius || a.i - b.i);
  const shelves: Shelf[] = [];
  let shelf: Shelf = { page: 0, top: 0, depth: 0, used: 0, items: [] };
  for (const { item } of order) {
    const d = Math.min(item.radius * 2, page.width);
    if (shelf.items.length > 0 && shelf.used + d > page.width) {
      shelves.push(shelf);
      shelf = { page: shelf.page, top: shelf.top + shelf.depth + page.gap, depth: 0, used: 0, items: [] };
    }
    // Sorted by size, so the first item on a shelf is its deepest: the shelf either fits the page now or never.
    if (shelf.items.length === 0 && shelf.top > 0 && shelf.top + d > page.depth) {
      shelf = { page: shelf.page + 1, top: 0, depth: 0, used: 0, items: [] };
    }
    shelf.items.push({ name: item.name, d });
    shelf.used += d + page.gap;
    shelf.depth = Math.max(shelf.depth, d);
  }
  if (shelf.items.length > 0) shelves.push(shelf);

  const out: Placement[] = [];
  for (const s of shelves) {
    // Each shelf's run is centred on the page, and its items stand on the shelf's centre line.
    let x = -(s.used - page.gap) / 2;
    for (const { name, d } of s.items) {
      out.push({ name, page: s.page, x: x + d / 2, z: s.top + s.depth / 2 - page.depth / 2 });
      x += d + page.gap;
    }
  }
  return out;
}

/** Pages a layout runs to. */
export function pageCount(placements: readonly Placement[]): number {
  return placements.reduce((n, p) => Math.max(n, p.page + 1), 0);
}
